import styles from "./DetailsContact.module.css";
import PropTypes from "prop-types";
import classNames from "classnames";
import { useCallback, useState } from "react";
import { TabBar, Button, CircleLoader } from "@wwg/ui";
import { useTranslation } from "react-i18next";
import { hasBitwise } from "shared/utils/hasBitwise";
import { CONTACT_TYPE } from "shared/enums";
import { ContactEdit } from "shared/components/ContactEdit/ContactEdit";
import { QueryParams } from "shared/hooks/useQueryParams";
import { useFreshState } from "shared/hooks/useFreshState";
import { useContactDetails } from "./useContactDetails";
import { CustomerInfo } from "./CustomerInfo/CustomerInfo";
import { ProspectInfo } from "./ProspectInfo/ProspectInfo";
import { useHistory } from "react-router-dom";
import {
  Modal,
  ModalContent,
  ModalControls,
} from "shared/components/Modal/Modal";

export const TABS = {
  Customer: "customer",
  Prospecting: "prospecting",
};

/**
 * Displays detailed info for the selected customer contact.
 */
export function DetailsContact({ id, linkBase }) {
  const { t } = useTranslation();
  const query = QueryParams.useContainer();
  const history = useHistory();

  const [selected] = useFreshState(query.params.tab || TABS.Customer);
  const [isEditing, setIsEditing] = useState(false);
  const [modalError, setModalError] = useState(null);

  const {
    contact,
    isLoading,
    isPreviousData,
    error,
    saveContactMutation,
    archiveContactMutation,
    unarchiveContactMutation,
  } = useContactDetails(id);

  const _onCancel = useCallback(() => {
    setIsEditing(false);
  }, []);

  const _onSave = useCallback(
    (data) => {
      saveContactMutation
        .mutateAsync(data)
        .then(() => {
          setIsEditing(false);
        })
        .catch(setModalError);
    },
    [saveContactMutation]
  );

  const _onArchive = useCallback(() => {
    archiveContactMutation.mutateAsync(contact.id).then(() => {
      history.replace(`/archive/customers/${contact.id}`);
    });
  }, [contact]);

  const _onUnarchive = useCallback(() => {
    unarchiveContactMutation.mutateAsync(contact.id).then(() => {
      history.replace(`/contacts/customers/${contact.id}`);
    });
  }, [contact]);

  if (error) {
    console.error(error);
    return "ERROR";
  }

  return (
    <div className={styles.container}>
      {!isLoading &&
        (isEditing ? (
          <ContactEdit
            contact={contact}
            onCancel={_onCancel}
            onSave={_onSave}
            onArchive={_onArchive}
          />
        ) : (
          <>
            <TabBar
              selected={selected}
              tabs={[
                {
                  label: t("labels.tabCustomerInfo"),
                  value: TABS.Customer,
                  onClick: () => query.set("tab", TABS.Customer),
                },
                {
                  label: t("labels.tabProspectingInfo"),
                  value: TABS.Prospecting,
                  onClick: () => query.set("tab", TABS.Prospecting),
                },
              ]}
            />

            <div className={styles.content}>
              {selected === TABS.Customer && (
                <CustomerInfo
                  contact={contact}
                  onEditClicked={() => {
                    setIsEditing(true);
                  }}
                  onUnarchiveClicked={_onUnarchive}
                />
              )}

              {selected === TABS.Prospecting &&
                (hasBitwise(CONTACT_TYPE.Prospect, contact.type) ? (
                  <ProspectInfo
                    contact={contact}
                    onEditClicked={() => {
                      setIsEditing(true);
                    }}
                    onUnarchiveClicked={_onUnarchive}
                  />
                ) : hasBitwise(CONTACT_TYPE.Archived, contact.type) ? (
                  <div className={styles.addProspect}>
                    <p>{t("labels.prospectNotAdded")}</p>
                  </div>
                ) : (
                  <div className={styles.addProspect}>
                    <p>{t("labels.prospectAddConfirm")}</p>
                    <div className={styles.addProspectButtons}>
                      <Button
                        disabled={saveContactMutation.isLoading}
                        buttonStyle="hollow"
                        onClick={(e) => {
                          e.preventDefault();
                          query.set("tab", TABS.Customer);
                        }}
                      >
                        {t("buttons.no")}
                      </Button>
                      <Button
                        disabled={saveContactMutation.isLoading}
                        buttonStyle="accent"
                        onClick={(e) => {
                          e.preventDefault();
                          saveContactMutation.mutate({
                            ...contact,
                            type: contact.type + CONTACT_TYPE.Prospect,
                          });
                        }}
                      >
                        {t("buttons.yes")}
                      </Button>
                    </div>
                  </div>
                ))}
            </div>
          </>
        ))}

      <div
        className={classNames(styles.loadingShade, {
          [styles.isLoading]: isLoading || isPreviousData,
        })}
      >
        <CircleLoader isLoading={isLoading || isPreviousData} />
      </div>

      <Modal isOpen={modalError}>
        <ModalContent>
          <p>{modalError?.message}</p>
        </ModalContent>
        <ModalControls>
          <Button
            onClick={() => {
              setModalError(null);
            }}
          >
            OK
          </Button>
        </ModalControls>
      </Modal>
    </div>
  );
}

DetailsContact.propTypes = {
  id: PropTypes.string.isRequired,
  linkBase: PropTypes.string.isRequired,
};
